import { Clock } from 'lucide-react';
import { Z } from '../shared/styles/zLayers'

/**
 * Session Timeout Warning Modal
 * Displays a countdown and allows user to stay logged in or logout
 *
 * ── TWO CLOCKS CAN END A SESSION, AND THEY NEED DIFFERENT WARNINGS ───────────────────────────
 * `reason` is 'idle' or 'absolute' (see shared/api/sessionPolicy.js, `endReason`).
 *
 * IDLE is the original case: nobody is at the keyboard, so blocking the page costs nothing and
 * "Keep Working" genuinely keeps them working.
 *
 * ABSOLUTE is the session simply having been alive as long as the company allows. Everything about
 * the idle presentation is wrong for it:
 *   - THE OVERLAY MUST NOT BLOCK. This person is mid-task — that is the whole difference — and a
 *     full-screen blocker for the last two minutes would stop them doing the one thing the warning
 *     is telling them to do, which is save their work. It would be worse than the silent sign-out
 *     it replaces.
 *   - "KEEP WORKING" WOULD BE A LIE. Nothing the browser can do extends the absolute window; the
 *     server refuses the refresh once it is spent. Offering the button would reproduce the oldest
 *     complaint about this app — pressing "stay logged in" and being thrown out anyway.
 *   - "DUE TO INACTIVITY" WOULD ALSO BE A LIE, and an insulting one to somebody who has been
 *     working all day.
 */
function SessionTimeoutWarning({ secondsRemaining, reason = 'idle', onStayLoggedIn, onLogout }) {
  const isAbsolute = reason === 'absolute';

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Overlay to block underlying interactions — idle only. See the header. */}
      {!isAbsolute && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: Z.modal,
            backdropFilter: 'blur(2px)'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      
      {/* Warning notification */}
      <div 
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: Z.dialog,
          animation: 'slideInRight 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
      <div 
        className="bfs-card p-6 mb-6 animate-scale-in"
        style={{
          maxWidth: '380px',
          padding: '1.25rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e5e7eb'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              backgroundColor: '#f3f4f6',
              padding: '0.5rem',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock size={20} style={{ color: 'var(--primary-color)' }} />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.125rem' }}>
                {isAbsolute ? 'Time to sign in again' : 'Still there?'}
              </h3>
              <p style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                Session ends in {formatTime(secondsRemaining)}
              </p>
            </div>
          </div>
        </div>

        {/* Message */}
        <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.5', marginBottom: '1rem' }}>
          {isAbsolute
            ? 'This session has been open as long as your company allows, so you need to sign in again. Save what you are working on first — this window will not stop you.'
            : 'You’ll be logged out soon due to inactivity.'}
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {isAbsolute ? (
            // ONE BUTTON, because there is no choice to offer. Signing in again is the only thing
            // that ends this state, and a "Keep Working" here could not keep anybody working.
            <button
              onClick={onLogout}
              className="bfs-button-primary"
              style={{
                flex: 1,
                fontSize: '0.875rem',
                padding: '0.5rem 1rem'
              }}
            >
              Sign in again
            </button>
          ) : (
            <>
              <button
                onClick={onLogout}
                className="bfs-button-secondary"
                style={{
                  flex: 1,
                  fontSize: '0.875rem',
                  padding: '0.5rem 1rem'
                }}
              >
                Logout
              </button>
              <button
                onClick={onStayLoggedIn}
                className="bfs-button-primary"
                style={{
                  flex: 1,
                  fontSize: '0.875rem',
                  padding: '0.5rem 1rem'
                }}
              >
                Keep Working
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      </div>
    </>
  );
}

export default SessionTimeoutWarning;
